import type { IPoint } from 'fabric/fabric-impl';
import { setZoom } from './zoom';
import type WhiteboardController from '../controllers/WhiteboardController';

// NOTE: TouchEventManager handles pinch-to-zoom and two-finger pan gestures for touch devices
// Extracted from Canvas to improve separation of concerns and code organization
export default class TouchEventManager {
  private touchStartDistance = 0;
  private touchStartCenter: IPoint | null = null;
  private lastTouchPositions: Touch[] = [];

  private readonly controller: WhiteboardController;
  private readonly containerElement: HTMLElement;
  private readonly canvas: {
    getZoom(): number;
    viewportTransform?: number[];
    setViewportTransform(vpt: number[]): void;
    fire(event: string): void;
  };

  // Bound event handler methods for proper cleanup
  private readonly boundHandleTouchStart: (event: TouchEvent) => void;
  private readonly boundHandleTouchMove: (event: TouchEvent) => void;
  private readonly boundHandleTouchEnd: (event: TouchEvent) => void;

  constructor(
    controller: WhiteboardController,
    containerElement: HTMLElement,
    canvas: {
      getZoom(): number;
      viewportTransform?: number[];
      setViewportTransform(vpt: number[]): void;
      fire(event: string): void;
    }
  ) {
    this.controller = controller;
    this.containerElement = containerElement;
    this.canvas = canvas;

    // Bind event handlers to maintain proper context
    this.boundHandleTouchStart = this.handleTouchStart.bind(this);
    this.boundHandleTouchMove = this.handleTouchMove.bind(this);
    this.boundHandleTouchEnd = this.handleTouchEnd.bind(this);
  }

  // Set up touch event listeners
  initialize(): void {
    this.containerElement.addEventListener(
      'touchstart',
      this.boundHandleTouchStart,
      { passive: false }
    );
    this.containerElement.addEventListener(
      'touchmove',
      this.boundHandleTouchMove,
      { passive: false }
    );
    this.containerElement.addEventListener(
      'touchend',
      this.boundHandleTouchEnd,
      { passive: false }
    );
  }

  // Clean up touch event listeners
  destroy(): void {
    this.containerElement.removeEventListener(
      'touchstart',
      this.boundHandleTouchStart
    );
    this.containerElement.removeEventListener(
      'touchmove',
      this.boundHandleTouchMove
    );
    this.containerElement.removeEventListener(
      'touchend',
      this.boundHandleTouchEnd
    );

    // Reset touch state
    this.touchStartDistance = 0;
    this.touchStartCenter = null;
    this.lastTouchPositions = [];
  }

  // Handle touch start events - prepare for two-finger gestures
  private handleTouchStart(event: TouchEvent): void {
    if (event.touches.length === 2) {
      event.preventDefault();

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      // Calculate initial distance between touches
      this.touchStartDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      // Calculate center point between touches
      this.touchStartCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      // Store initial touch positions
      this.lastTouchPositions = Array.from(event.touches);
    }
  }

  // Handle touch move events - process pinch zoom and two-finger pan
  private handleTouchMove(event: TouchEvent): void {
    if (!this.controller.allowPanAndZoom()) {
      return;
    }

    if (event.touches.length === 2 && this.touchStartDistance > 0) {
      event.preventDefault();

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      // Calculate current distance and center
      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      const currentCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      // Handle pinch zoom
      if (this.touchStartDistance > 0) {
        const scale = currentDistance / this.touchStartDistance;
        const currentZoom = this.canvas.getZoom();
        const newZoom = Math.min(Math.max(currentZoom * scale, 0.1), 5); // Clamp zoom between 0.1x and 5x

        // Convert touch coordinates to canvas coordinates
        const canvasRect = this.containerElement.getBoundingClientRect();
        const zoomPoint = {
          x: currentCenter.x - canvasRect.left,
          y: currentCenter.y - canvasRect.top,
        };

        setZoom(this.controller, newZoom, zoomPoint, false);
        this.touchStartDistance = currentDistance;
      }

      // Handle two-finger pan
      if (this.touchStartCenter && this.lastTouchPositions.length === 2) {
        const deltaX = currentCenter.x - this.touchStartCenter.x;
        const deltaY = currentCenter.y - this.touchStartCenter.y;

        // Only pan if the movement is significant (reduces jitter)
        if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
          const vpt = this.canvas.viewportTransform;
          if (vpt) {
            vpt[4] += deltaX;
            vpt[5] += deltaY;
            this.canvas.setViewportTransform(vpt);
            this.canvas.fire('pan');

            this.touchStartCenter = currentCenter;
          }
        }
      }

      this.lastTouchPositions = Array.from(event.touches);
    }
  }

  // Handle touch end events - reset touch tracking when fewer than 2 fingers
  private handleTouchEnd(event: TouchEvent): void {
    if (event.touches.length < 2) {
      // Reset touch tracking when fewer than 2 fingers
      this.touchStartDistance = 0;
      this.touchStartCenter = null;
      this.lastTouchPositions = [];
    }
  }
}
